import { selectGroup, selectQueryView, selectSelectedViewId, selectStatus, selectTag } from "@redux/memoizedSelectors";
import { checkStyle } from "@utils/helpers";
import { EntityList } from "@utils/optionsSets/OptionSets";
import GroupsCxAttribute from "features/grid-table/columns-components/GroupsCxAttribute";
import { JourneyMaps } from "features/grid-table/columns-components/JourneyMaps";
import TagsCxAttribute from "features/grid-table/columns-components/TagsCxAttribute";
import { PickListMultipleTemplate, PickListSingleFilterTemplate, SolutionTypeFilter, balanceFilterTemplate, dateBodyTemplate, dateFilterTemplate } from "features/grid-table/components/TableSettings";
import { t } from "i18next";
import { useSelector } from "react-redux";
import { groupUniqueIdsWithNames } from "utils/helpers";
import { DateCxAttributeColumn, DatePicker, Description, StatusCxAttributeColumn, Subject, displayBenefitCard } from "../../columns-components/ReusableListComponents";
import User from "features/grid-table/columns-components/User";
import { retrieveAllActiveSolutions } from "redux/memoizedSelectors";

export const GetSolutionColumns = (props) => {
	const context = props.context.split(".")[1];
	const { dataKey } = props?.contextData;

	const group = useSelector(selectGroup);
	const tag = useSelector(selectTag);
	const statusTypes = useSelector(selectStatus({ entity: "solutions" }));
	const maps = useSelector((state) => state.maps.all_maps);
	const selectedViewId = useSelector((state) => selectSelectedViewId(state, EntityList.Solution, context));
	const queryView = useSelector((state) => selectQueryView(state, EntityList.Solution))?.getById(selectedViewId)?.columnSetting;
	const authorizedUsers = useSelector((state) => state.settings.currentWorkspaceAuthorizedUsers);
	const users = useSelector((state) => state.settings.users);

	const filteredUsers = authorizedUsers.filter((user) => {
		const userDetails = users?.find((u) => u?.loginName?.toLowerCase() === user?.domainName?.toLowerCase());

		return userDetails?.isActive;
	})?.FormatFilterArray("id","fullName");
	const isSample = context === "sampleData";
	const list = props?.contextData?.listData;
	const isColumnEditable = props?.contextData?.listEditable && context !== "cxProgram";

	const journeyMapFilters = groupUniqueIdsWithNames({ objects: list, maps });

	const solutionTypes = [
		{ value: 100000000, name: t("QUICK_WIN") },
		{ value: 100000002, name: t("NICE_TO_HAVE") },
		{ value: 100000001, name: t("COMPLEX_PROJ") },
		{ value: 100000003, name: t("TO_AVOID") },
	];

	return {
		name: {
			field: "name",
			header: t("SOLUTION_NAME"),
			body: (data) => Subject(data, { route: props?.contextData?.route, allowClick: props?.contextData?.clickToOpen }),
			sortable: true,
			filterField: "name",
			filter: true,
			showFilterOperator: false,
			style: checkStyle(queryView, "name", isSample ? "xxxl" : "xxl"),
			className: "py-0",
		},
		description: {
			field: "description",
			sortable: true,
			header: t("ASPX_9"),
			dataType: "text",
			style: checkStyle(queryView, "description", "m"),
			body: (data) => Description(data),
		},
		cjmIds: {
			field: "cjmIds",
			header: t("JOURNEY_MAP"),
			filterField: "cjmIdsStr",
			sortable: true,
			showAddButton: false,
			showFilterOperator: false,
			body: (data) => JourneyMaps(data),
			showFilterMatchModes: false,
			filter: true,
			style: checkStyle(queryView, "cjmIds", "xl"),
			filterElement: (options) => PickListMultipleTemplate(options, journeyMapFilters, t("SELECT_VALUE")),
		},
		occurance: {
			field: "occurance",
			filterField: "occurance",
			header: t("OCCURANCE"),
			sortable: true,
			filter: true,
			showFilterOperator: false,
			filterElement: balanceFilterTemplate,
			dataType: "numeric",
			className: "py-0 text-center",
			style: checkStyle(queryView, "occurance", "s", "120px"),
		},
		statusId: {
			field: "statusId",
			filterField: "statusId",
			header: t("ASPX_222"),
			sortable: true,
			showFilterMatchModes: false,
			filter: true,
			style: checkStyle(queryView, "statusId", "m"),
			body: (data) =>
				StatusCxAttributeColumn({
					data,
					recordType: "cem_cxsolution",
					memoizedSelector: retrieveAllActiveSolutions,
					onUpdateCxAttribute: props?.contextData?.onUpdateCxAttributeMulti,
					isDisabled: !isColumnEditable,
				}),
			filterElement: (options) => PickListMultipleTemplate(options, statusTypes),
		},
		solutionType: {
			field: "solutionType",
			header: t("JS_78"),
			sortable: true,
			showFilterOperator: false,
			showFilterMatchModes: false,
			showAddButton: false,
			filter: true,
			filterField: "solutionType",
			style: checkStyle(queryView, "solutionType", "m"),
			body: (solution) => displayBenefitCard(solution),
			filterElement: (options) => SolutionTypeFilter(options, solutionTypes),
		},
		tagIds: {
			field: "tagIds",
			filterField: "tagIds",
			header: t("TAGS"),
			sortable: true,
			showFilterOperator: false,
			showFilterMatchModes: false,
			showAddButton: false,
			filter: true,
			body: (data) => TagsCxAttribute({ data, allRecordsSelector: "solutions.all_solutions" }),
			style: checkStyle(queryView, "tagIds", "m"),
			filterElement: (options) => PickListMultipleTemplate(options, tag, t("SELECT_TAG")),
		},
		groupIds: {
			field: "groupIds",
			filterField: "groupIds",
			header: t("GROUPS"),
			sortable: true,
			showFilterOperator: false,
			showFilterMatchModes: false,
			showAddButton: false,
			filter: true,
			body: (data) => GroupsCxAttribute({ data, allRecordsSelector: "solutions.all_solutions" }),
			style: checkStyle(queryView, "groupIds", "m"),
			filterElement: (options) => PickListMultipleTemplate(options, group, t("SELECT_GROUP")),
		},
		lastUpdated: {
			field: "lastUpdated",
			filterField: "lastUpdated",
			header: t("JS_72"),
			sortable: true,
			showFilterOperator: false,
			filter: true,
			style: checkStyle(queryView, "lastUpdated", "m"),
			dataType: "date",
			body: (data) => dateBodyTemplate(data.lastUpdated, "action"),
			filterElement: dateFilterTemplate,
		},
		dueDateNew: {
			field: "dueDateNew",
			filterField: "dueDateNew",
			header: t("DUE_DATE"),
			sortable: true,
			showFilterOperator: false,
			dataType: "date",
			filter: true,
			style: checkStyle(queryView, "dueDateNew", "m"),
			body: (data) =>
				DateCxAttributeColumn({
					data,
					memoizedSelector: retrieveAllActiveSolutions,
					onUpdateCxAttribute: props?.contextData?.onUpdateCxAttributeMulti,
					isDisabled: !isColumnEditable,
				}),
			filterElement: dateFilterTemplate,
		},
		userId: {
			field: "userId",
			header: t("CS_35"),
			body: User,
			filterField: "userId",
			filter: true,
			showFilterOperator: false,
			showFilterMatchModes: false,
			filterElement: (options) => PickListMultipleTemplate(options, filteredUsers),
			style: checkStyle(queryView, "userId", "xs", "96px"),
		},
	};
};
